import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  fetchLmeDraftEmail,
  fetchLmePersonalizeIntro,
  fetchSendGuard,
  sendLmeEmail,
  type SendGuard,
} from "../../api";
import type { DraftEmailResponse, MatchedLender } from "../../types/lme";
import { MicrosoftReconnect } from "../shared/MicrosoftReconnect";

interface LmeDraftPreviewModalProps {
  dealId: string;
  lender: MatchedLender;
  onCancel: () => void;
  onToast: (msg: string) => void;
  /** Phase C — called after a successful send so the parent can close the modal
   *  and refresh the state pill (lenders-pitched count). */
  onSent: () => void;
}

/**
 * Per-lender draft preview.
 *
 * Phase B adds:
 *   - Editable intro textarea — user can type their own intro OR ask AI to
 *     tweak via "✦ TWEAK WITH AI" button. Textarea value is the source of
 *     truth for `intro_paragraph_override` sent to /draft-email.
 *   - REFRESH PREVIEW button re-fetches the rendered HTML with the current
 *     textarea value applied. Avoids surprise stale-preview-then-copy bugs.
 */
export function LmeDraftPreviewModal({
  dealId,
  lender,
  onCancel,
  onToast,
  onSent,
}: LmeDraftPreviewModalProps) {
  const [draft, setDraft] = useState<DraftEmailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tweaking, setTweaking] = useState(false);
  const [intro, setIntro] = useState<string>("");
  const [view, setView] = useState<"html" | "plain">("html");
  const [error, setError] = useState<string | null>(null);
  // Phase C send state: confirmingSend swaps the footer to the inline confirm
  // strip; sending disables everything during the Graph call.
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [sending, setSending] = useState(false);
  // LME-4(c): the shared self-test send guard. When active, every send is
  // redirected to the test address and the lender is never emailed.
  const [guard, setGuard] = useState<SendGuard | null>(null);
  // LME-4(a): Microsoft connection — null=checking, false=disconnected (gates SEND).
  const [msConnected, setMsConnected] = useState<boolean | null>(null);

  useEffect(() => {
    fetchSendGuard().then(setGuard).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Esc cancels the send-confirm first; otherwise closes the modal (never
      // mid-flight).
      if (e.key === "Escape" && !tweaking && !loading && !sending) {
        if (confirmingSend) setConfirmingSend(false);
        else onCancel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, tweaking, loading, sending, confirmingSend]);

  const loadDraft = async (introOverride: string) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchLmeDraftEmail({
        deal_id: dealId,
        lender_account_id: lender.account_id,
        intro_paragraph_override: introOverride.trim() || null,
      });
      setDraft(d);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDraft("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, lender.account_id]);

  const onTweak = async () => {
    setTweaking(true);
    setError(null);
    try {
      // Phase B.2 FIX 3: pass the user's typed draft (if any) so worker
      // rewrites it instead of generating fresh. Empty textarea falls back
      // to the original generate-from-scratch behavior.
      const draft = intro.trim();
      const r = await fetchLmePersonalizeIntro({
        deal_id: dealId,
        lender_account_id: lender.account_id,
        current_intro: draft || undefined,
      });
      if (!r.intro_paragraph) {
        onToast("Nothing meaningful to personalize for this lender.");
      } else {
        setIntro(r.intro_paragraph);
        // Don't auto-reload the preview — user can review the rewritten
        // intro and click APPLY TO EMAIL when ready. Matches the three-step
        // flow: type → tweak → apply.
        onToast(draft ? "✓ Intro rewritten" : "✓ Intro generated");
      }
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setTweaking(false);
    }
  };

  const onApplyToEmail = () => {
    void loadDraft(intro);
  };

  const recipientEmail = draft?.to.email ?? null;
  const recipientName = draft?.to.name ?? null;

  const onSend = async () => {
    if (!draft || !recipientEmail) return;
    setSending(true);
    setError(null);
    try {
      const r = await sendLmeEmail({
        deal_id: dealId,
        lender_account_id: lender.account_id,
        to: recipientEmail,
        subject: draft.subject,
        body_html: draft.html_body,
        body_plain: draft.plain_body,
      });
      const who = `${lender.account_name}${recipientName ? ` (${recipientName})` : ""}`;
      // LME-4(c): redirected test send — the lender was NOT emailed and no Zoho
      // side-effects fired. Say so plainly instead of a "synced/failed" toast.
      if (r.redirected) {
        onToast(`✓ TEST send → redirected to ${r.sent_to} — ${who} NOT emailed, nothing logged`);
        onSent();
        return;
      }
      // Email already went out. Compose a single toast from whichever
      // server-side syncs failed (pitch-log append and/or Submissions tracker);
      // on full success, surface the dropbox detail.
      const fails: string[] = [];
      if (r.log_pitch_lines_appended === 0) fails.push("pitch log");
      if (!r.submission_synced) fails.push("Submissions");
      if (fails.length === 0) {
        const tail = r.bcc_dropbox ? " — pitch logged + BCC dropbox" : " — pitch logged";
        onToast(`✓ Sent to ${who}${tail}`);
      } else {
        onToast(`⚠ Sent to ${who} — ${fails.join(" + ")} sync failed; add manually`);
      }
      onSent();
    } catch (err) {
      setError(formatErr(err));
      setConfirmingSend(false);
    } finally {
      setSending(false);
    }
  };

  const copyHtml = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft.html_body);
      onToast("✓ HTML copied");
    } catch {
      onToast("⚠ Copy failed");
    }
  };
  const copyPlain = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft.plain_body);
      onToast("✓ Text copied");
    } catch {
      onToast("⚠ Copy failed");
    }
  };

  const iframeSrcDoc = useMemo(() => {
    if (!draft) return "";
    return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:12px;font-family:Times New Roman,serif;background:#fff;color:#000;}</style></head><body>${draft.html_body}</body></html>`;
  }, [draft]);

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card lme-draft-card">
        <div className="write-confirm-title">
          DRAFT EMAIL · <span className="lme-draft-lender">{lender.account_name}</span>
        </div>

        {error ? (
          <div className="write-confirm-error">⚠ {error}</div>
        ) : null}

        {guard?.active && (
          <div className="email-guard-banner">
            ⚠ SEND GUARD ACTIVE — every send is redirected to{" "}
            <b>{guard.redirect_to}</b>. No email will reach the lender.
          </div>
        )}

        <MicrosoftReconnect onStatus={setMsConnected} />

        <div className="lme-draft-meta">
          {draft && (
            <>
              <div className="lme-draft-meta-row">
                <span className="lme-draft-meta-label">TO</span>
                <span>
                  {draft.to.name ?? "—"}
                  {draft.to.email ? ` <${draft.to.email}>` : ""}
                </span>
              </div>
              <div className="lme-draft-meta-row">
                <span className="lme-draft-meta-label">SUBJECT</span>
                <span>{draft.subject}</span>
              </div>
            </>
          )}
        </div>

        <div className="lme-draft-intro-row">
          <label className="lme-draft-intro-label">
            INTRO (replaces the default intro)
          </label>
          <textarea
            className="lme-draft-intro-textarea"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            placeholder="Optional — type your own one-liner OR click ✦ TWEAK WITH AI"
            rows={2}
            disabled={loading || tweaking}
          />
          <div className="lme-draft-intro-actions">
            <button
              className="lme-draft-personalize"
              onClick={onTweak}
              disabled={tweaking || loading}
              title={intro.trim() ? "Rewrite your draft more professionally" : "Generate a personalized intro for this lender"}
            >
              {tweaking ? "TWEAKING…" : "✦ TWEAK WITH AI"}
            </button>
            <button
              className="lme-draft-refresh"
              onClick={onApplyToEmail}
              disabled={loading || tweaking}
              title="Apply the current intro and re-render the email preview"
            >
              APPLY TO EMAIL
            </button>
          </div>
        </div>

        <div className="lme-draft-toolbar">
          <button
            className={clsxToggle(view === "html")}
            onClick={() => setView("html")}
          >
            HTML
          </button>
          <button
            className={clsxToggle(view === "plain")}
            onClick={() => setView("plain")}
          >
            PLAIN TEXT
          </button>
        </div>

        <div className="lme-draft-preview">
          {loading ? (
            <div className="lme-loading">
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-label">RENDERING</span>
            </div>
          ) : draft ? (
            view === "html" ? (
              <iframe
                className="lme-draft-iframe"
                title="Email preview"
                srcDoc={iframeSrcDoc}
                sandbox=""
              />
            ) : (
              <pre className="lme-draft-plain">{draft.plain_body}</pre>
            )
          ) : null}
        </div>

        {confirmingSend ? (
          <div className="lme-draft-send-confirm">
            <span className="lme-draft-send-confirm-text">
              {guard?.active ? (
                <>
                  TEST send → redirected to <strong>{guard.redirect_to}</strong>.{" "}
                  {recipientName ? `${recipientName} ` : ""}&lt;{recipientEmail}&gt;
                  will NOT receive this.
                </>
              ) : (
                <>
                  Send to{" "}
                  <strong>
                    {recipientName ? `${recipientName} ` : ""}&lt;{recipientEmail}&gt;
                  </strong>
                  ? <span className="lme-draft-send-bcc">+ BCC Zoho dropbox</span>
                </>
              )}
            </span>
            <button
              className="write-confirm-cancel"
              onClick={() => setConfirmingSend(false)}
              disabled={sending}
            >
              CANCEL
            </button>
            <button
              className="write-confirm-confirm lme-draft-send-go"
              onClick={() => void onSend()}
              disabled={sending}
            >
              {sending
                ? "SENDING…"
                : guard?.active
                  ? "✓ CONFIRM TEST SEND"
                  : "✓ CONFIRM SEND"}
            </button>
          </div>
        ) : (
          <div className="write-confirm-actions">
            <button
              className="write-confirm-cancel"
              onClick={onCancel}
              disabled={sending}
            >
              CLOSE
            </button>
            <button
              className="lme-draft-copy"
              onClick={view === "html" ? copyHtml : copyPlain}
              disabled={!draft || loading}
            >
              {view === "html" ? "COPY HTML" : "COPY TEXT"}
            </button>
            <button
              className="write-confirm-confirm lme-draft-send"
              onClick={() => setConfirmingSend(true)}
              disabled={
                !draft ||
                loading ||
                tweaking ||
                !recipientEmail ||
                msConnected === false
              }
              title={
                msConnected === false
                  ? "Microsoft disconnected — reconnect above to send"
                  : recipientEmail
                    ? "Send this email to the lender's primary contact"
                    : "No primary-contact email on file for this lender"
              }
            >
              SEND
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function clsxToggle(active: boolean): string {
  return "lme-draft-toggle" + (active ? " is-active" : "");
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } } | undefined;
    if (body?.error) {
      return `${body.error.code ?? "ERROR"}: ${body.error.message ?? ""} (HTTP ${err.status})`;
    }
    return `${err.code} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return "draft_failed";
}
